/* global __DEV__, __SERVER__ */
import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { isAuthorized as createCometAuthCheck } from '@zg-rentals/auth-comet';

type UserApiResponse = {
  loggedIn?: boolean;
  userToken?: string;
  email?: string;
  phoneNumber?: string;
  hasStrongAuthentication?: boolean;
  isEmailVerified?: boolean;
  isTwoFactorAuthEnabled?: boolean;
  isTwoFactorAuthenticated?: boolean;
  roles?: Array<string>;
  impersonatorUserToken?: string;
  impersonatorEmail?: string;
  isUserBlocked?: boolean;
  sessionSecondsRemaining?: number;
};

export type User = {
  loggedIn: boolean;
  userToken: string;
  email: string;
  phoneNumber: string;
  isEmailVerified: boolean;
  isTwoFactorAuthEnabled: boolean;
  isTwoFactorAuthenticated: boolean;
  hasStrongAuthentication: boolean;
  roles: Array<string>;
  impersonatorUserToken?: string;
  impersonatorEmail?: string;
  isUserBlocked: boolean;
  sessionExpirationDate: number;
};

export type AuthProps = {
  cookieHeader?: string;
  onResponseCookies?: (cookies: string) => void;
  brand?: string;
  clientIp?: string;
};

function normalizeUser({
  loggedIn = false,
  userToken = '',
  email = '',
  phoneNumber = '',
  hasStrongAuthentication = false,
  isEmailVerified = false,
  isTwoFactorAuthEnabled = false,
  isTwoFactorAuthenticated = false,
  roles = [],
  impersonatorUserToken = '',
  impersonatorEmail = '',
  isUserBlocked = false,
  sessionSecondsRemaining = 0,
}: UserApiResponse = {}): User {
  return {
    loggedIn: Boolean(loggedIn),
    userToken: String(userToken),
    email: String(email),
    phoneNumber: String(phoneNumber),
    isEmailVerified: Boolean(isEmailVerified),
    isTwoFactorAuthEnabled: Boolean(isTwoFactorAuthEnabled),
    isTwoFactorAuthenticated: Boolean(isTwoFactorAuthenticated),
    hasStrongAuthentication: Boolean(hasStrongAuthentication),
    roles: ([] as Array<string>).concat(roles),
    impersonatorUserToken: impersonatorUserToken ? String(impersonatorUserToken) : undefined,
    impersonatorEmail: impersonatorEmail ? String(impersonatorEmail) : undefined,
    isUserBlocked: Boolean(isUserBlocked),
    sessionExpirationDate: sessionSecondsRemaining ? Date.now() + sessionSecondsRemaining * 1000 : 0,
  };
}

const USER_API_URL = __SERVER__
  ? `${process.env.ZG_API}/renter-hub-api/api/v1/users/get`
  : __DEV__
    ? '/renter-hub/proxy/renter-hub-api/api/v1/users/get'
    : '/renter-hub-api/api/v1/users/get';

async function fetchUser(props: AuthProps): Promise<User> {
  const url = USER_API_URL;
  const headers: HeadersInit = {};
  if (props.cookieHeader) {
    headers.cookie = props.cookieHeader;
    if (props.brand) {
      headers['x-brand'] = props.brand;
    }
    if (props.clientIp) {
      headers['x-client-ip'] = props.clientIp;
    }
  }

  const response = await fetch(url, {
    credentials: 'same-origin',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.status}`);
  }

  const setCookie = response.headers.get('set-cookie');
  if (setCookie && props.onResponseCookies) {
    props.onResponseCookies(setCookie);
  }

  const data = await response.json();
  return normalizeUser(data.response || data);
}

const isCometAuthorized = createCometAuthCheck({
  mapUser: (user: User) => ({ ...user, isLoggedIn: user.loggedIn }),
});

export const Auth = defineAuthProvider<AuthProps, User>({
  queryOptions: (props) => ({
    queryFn: () => fetchUser(props),
    staleTime: 30_000,
    refetchInterval: 3 * 60 * 1000,
    retry: false,
  }),
  isAuthorized: (user) => {
    if (user.isUserBlocked) return false;
    return isCometAuthorized(user);
  },
});

export const { useAuth } = Auth;
